import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Facebook, 
  Instagram, 
  MapPin, 
  Phone, 
  Mail,
  Send,
  ArrowRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { useSettings } from '../../context/SettingsContext';

const quickLinks = [
  { name: 'Home', path: '/' },
  { name: 'Tours', path: '/tours' },
  { name: 'About Us', path: '/about' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'Contact', path: '/contact' },
  { name: 'FAQs', path: '/faqs' },
];

const popularDestinations = [
  { name: 'Goa Beach Tours', path: '/tours?search=goa' },
  { name: 'Kerala Backwaters', path: '/tours?search=kerala' },
  { name: 'Rajasthan Heritage', path: '/tours?search=rajasthan' },
  { name: 'Kashmir Paradise', path: '/tours?search=kashmir' },
  { name: 'Andaman Islands', path: '/tours?search=andaman' },
  { name: 'Himalayan Treks', path: '/tours?search=himalayan' },
];

export function SiteFooter() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { settings, loading } = useSettings();

  const companyInfo = settings?.company_info || {};
  const socialMedia = settings?.social_media || {};
  
  const companyName = companyInfo.name || 'NYRAV TOURS AND TRAVELS';
  const phone = companyInfo.phone || '+91 9181268900';
  const companyEmail = companyInfo.email || 'nyravtravels@gmail.com';
  const address = companyInfo.address || '44, PB Road, Suhagpur, Rehabari, Guwahati-08';
  const tagline = companyInfo.tagline || 'Your gateway to unforgettable adventures.';

  const socialLinks = [
    { icon: Facebook, href: socialMedia.facebook || 'https://facebook.com', label: 'Facebook' },
    { icon: Instagram, href: socialMedia.instagram || 'https://instagram.com', label: 'Instagram' },
  ];

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      toast.success('Thank you for subscribing to our newsletter!');
      setEmail('');
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <footer className="bg-gradient-to-b from-[#003847] to-[#002530] text-white" data-testid="site-footer">
      {/* Newsletter Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="bg-gradient-to-r from-[#FF6B35]/20 to-[#FF6B35]/5 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 
                className="text-2xl md:text-3xl font-bold mb-2"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Subscribe to Our Newsletter
              </h3>
              <p className="text-white/70">
                Get exclusive travel deals, tips, and destination guides delivered to your inbox
              </p>
            </div>
            <form 
              onSubmit={handleNewsletterSubmit}
              className="flex flex-col sm:flex-row gap-3 w-full md:w-auto"
              data-testid="newsletter-form"
            >
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-full px-6 py-3 min-w-[280px]"
                data-testid="newsletter-email-input"
              />
              <Button 
                type="submit"
                disabled={isSubmitting}
                className="bg-[#FF6B35] hover:bg-[#E85A2D] text-white rounded-full px-8 py-3 font-semibold whitespace-nowrap"
                data-testid="newsletter-submit-btn"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                <Send className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* About Column */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              {settings.logo ? (
                <img 
                  src={settings.logo} 
                  alt={companyName}
                  className="h-12 w-auto object-contain"
                />
              ) : (
                <>
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FF6B35] to-[#E85A2D] rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-xl">{companyName[0]}</span>
                  </div>
                  <div>
                    <span 
                      className="text-2xl font-bold" 
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {companyName.split(' ')[0]}
                    </span>
                    <span className="text-xs block text-white/60 -mt-1 tracking-widest">
                      {companyName.split(' ').slice(1).join(' ') || 'TOURS & TRAVELS'}
                    </span>
                  </div>
                </>
              )}
            </div>
            <p className="text-white/70 mb-6 leading-relaxed">
              {tagline} We craft personalized travel experiences 
              that create lifelong memories, from exotic beaches to majestic mountains.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a 
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#FF6B35] hover:scale-110 transition-all duration-200" 
                  data-testid={`social-${social.label.toLowerCase()}`}
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 
              className="text-lg font-semibold mb-6 flex items-center gap-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <span className="w-8 h-0.5 bg-[#FF6B35]"></span>
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-white/70 hover:text-[#FF6B35] transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity -ml-6 group-hover:ml-0" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Destinations */}
          <div>
            <h4 
              className="text-lg font-semibold mb-6 flex items-center gap-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <span className="w-8 h-0.5 bg-[#FF6B35]"></span>
              Popular Destinations
            </h4>
            <ul className="space-y-3">
              {popularDestinations.map((dest) => (
                <li key={dest.name}>
                  <Link 
                    to={dest.path} 
                    className="text-white/70 hover:text-[#FF6B35] transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity -ml-6 group-hover:ml-0" />
                    {dest.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 
              className="text-lg font-semibold mb-6 flex items-center gap-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              <span className="w-8 h-0.5 bg-[#FF6B35]"></span>
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#FF6B35]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <MapPin className="w-5 h-5 text-[#FF6B35]" />
                </div>
                <div className="text-white/70">
                  {address.split(',').map((part, i) => (
                    <span key={i} className="block">{part.trim()}</span>
                  ))}
                </div>
              </li>
              <li className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#FF6B35]/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#FF6B35]" />
                </div>
                <div>
                  <a href={`tel:${phone.replace(/\s/g, '')}`} className="text-white/70 hover:text-[#FF6B35] transition-colors block">
                    {phone}
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-4">
                <div className="w-10 h-10 bg-[#FF6B35]/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#FF6B35]" />
                </div>
                <div>
                  <a href={`mailto:${companyEmail}`} className="text-white/70 hover:text-[#FF6B35] transition-colors block">
                    {companyEmail}
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-sm text-center md:text-left">
            © {new Date().getFullYear()} {companyName}. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <Link to="/faqs" className="text-white/50 hover:text-white transition-colors">
              FAQs
            </Link>
            <a href="#" className="text-white/50 hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="text-white/50 hover:text-white transition-colors">
              Terms of Service
            </a>
            <a href="#" className="text-white/50 hover:text-white transition-colors">
              Cancellation Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
